// ─────────────────────────────────────────────────────────────────────────────
// action_scroll.ts — Infinite-scroll renderer for adventure action page
//
// Blocks append from the bottom at ~mid-height.  Old blocks disappear off
// the top.  Each block is laid out vertically, measured, then composited
// bottom-up.  The scroll can be driven by user drag or auto-scrolls to the
// newest block.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  ActionBlock, ActionBlockOption, AdventureState, DialogSpeaker,
} from '@shared/adventure_model';

// ── Constants ────────────────────────────────────────────────────────────────

const BLOCK_PADDING    = 14;
const BLOCK_GAP        = 10;
const BLOCK_INSET      = 40;   // left/right margin
const DIALOG_LINE_H    = 16;
const OPTION_LINE_H    = 26;
const SEPARATOR_H      = 20;
const IMAGE_BLOCK_H    = 160;
const INTERACTIVE_H    = 200;
const FADE_ZONE        = 80;   // px from top where blocks start fading
const TIMER_ICON_SIZE  = 22;

// ── Speaker palette ──────────────────────────────────────────────────────────

const SPEAKER_STYLE: Record<string, { font: string; color: string; bgTint: string; label: string }> = {
  ireth:  { font: '11px "Georgia", serif',          color: '#3a2a10', bgTint: 'rgba(200,180,140,0.12)', label: 'Ireth' },
  laura:  { font: '11px "Segoe UI", sans-serif',    color: '#5a3a20', bgTint: 'rgba(200,160,100,0.10)', label: 'Laura' },
  system: { font: 'italic 10px "Georgia", serif',   color: '#8a7a5a', bgTint: 'rgba(160,150,130,0.06)', label: '' },
};

// ── Hit rect for interactive elements ────────────────────────────────────────

export interface ScrollHitRect {
  kind: 'clock-skip' | 'option';
  blockId: string;
  optionId?: string;
  x: number; y: number; w: number; h: number;
}

// ── Block height measurement ─────────────────────────────────────────────────

/**
 * Measure a block's rendered height, caching the result on `block.height`.
 * Needs a ctx for text measurement.
 */
function measureBlock(
  ctx: CanvasRenderingContext2D,
  block: ActionBlock,
  contentW: number,
): number {
  if (block.height > 0) return block.height;

  let h = BLOCK_PADDING * 2; // top + bottom padding

  switch (block.kind) {
    case 'dialog': {
      const style = SPEAKER_STYLE[block.speaker ?? 'system'] ?? SPEAKER_STYLE.system;
      if (style.label) h += 14; // speaker label line
      ctx.font = style.font;
      h += wrapTextHeight(ctx, block.text ?? '', contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
      break;
    }
    case 'narration': {
      ctx.font = 'italic 10px "Georgia", serif';
      h += wrapTextHeight(ctx, block.text ?? '', contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
      break;
    }
    case 'option-group': {
      if (block.text) {
        ctx.font = '11px "Georgia", serif';
        h += wrapTextHeight(ctx, block.text, contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
        h += 6;
      }
      h += (block.options?.length ?? 0) * OPTION_LINE_H;
      break;
    }
    case 'image':
      h = IMAGE_BLOCK_H;
      break;
    case 'interactive':
      h = INTERACTIVE_H;
      break;
    case 'separator':
      h = SEPARATOR_H;
      break;
  }

  // Timer row
  if (block.timerMs > 0) {
    h += TIMER_ICON_SIZE + 4;
  }

  block.height = h;
  return h;
}

/** Calculate wrapped text height for a given max width. */
function wrapTextHeight(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
  lineH: number,
): number {
  const words = text.split(' ');
  let lines = 1;
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      lines++;
      line = word;
    } else {
      line = test;
    }
  }
  return lines * lineH;
}

// ── Draw helpers ─────────────────────────────────────────────────────────────

/** Word-wrap and draw text, returning total height used. */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number, y: number,
  maxW: number,
  lineH: number,
): number {
  const words = text.split(' ');
  let cy = y;
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, cy);
      cy += lineH;
      line = word;
    } else {
      line = test;
    }
  }
  if (line) {
    ctx.fillText(line, x, cy);
    cy += lineH;
  }
  return cy - y;
}

// ── Main draw function ───────────────────────────────────────────────────────

/**
 * Render the action scroll onto the canvas.
 *
 * Fixed window — no user scrolling.  Newest blocks sit at bottom-center,
 * older blocks slide up and fade out at the top.  Once gone, they're gone
 * (the History tab is the archive).
 *
 * @param ctx     Canvas rendering context (already translated to page origin)
 * @param state   Adventure state with actionBlocks
 * @param pageW   Page width in canvas units
 * @param pageH   Page height in canvas units
 * @param clockMs Current adventure clockMs (for timer countdowns)
 * @returns Hit rectangles for interactive elements (clock icons, options)
 */
export function drawActionScroll(
  ctx: CanvasRenderingContext2D,
  state: AdventureState,
  pageW: number,
  pageH: number,
  clockMs: number,
): ScrollHitRect[] {
  const blocks = state.actionBlocks;
  const hitRects: ScrollHitRect[] = [];

  if (blocks.length === 0) {
    // Empty state — prompt text
    ctx.save();
    ctx.fillStyle = '#8a7a5a';
    ctx.font = 'italic 12px "Georgia", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('The page is blank. Something stirs\u2026', pageW / 2, pageH / 2);
    ctx.restore();
    return hitRects;
  }

  const contentW = pageW - BLOCK_INSET * 2;

  // Measure all blocks
  ctx.save();
  for (const b of blocks) {
    measureBlock(ctx, b, contentW);
  }

  // Fixed window: newest block's bottom edge sits at ~55% page height.
  // Walk upward — anything that falls above the page top simply isn't drawn.
  const anchorY = pageH * 0.55;
  let blockBottom = anchorY;

  // Draw from newest (end) to oldest (start) — older is further up
  ctx.textBaseline = 'top';

  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i];
    const bh = block.height;
    const by = blockBottom - bh;
    blockBottom = by - BLOCK_GAP;

    // Skip if completely below viewport
    if (by > pageH + 10) continue;
    // Stop if completely above viewport
    if (by + bh < -10) break;

    // Compute fade for blocks near the top
    let alpha = 1;
    if (by < FADE_ZONE) {
      alpha = Math.max(0, by / FADE_ZONE);
    }
    if (alpha <= 0) continue;

    ctx.save();
    ctx.globalAlpha = alpha;

    const bx = BLOCK_INSET;

    // Block background
    drawBlockBackground(ctx, block, bx, by, contentW, bh);

    // Block content
    const textX = bx + BLOCK_PADDING;
    let textY = by + BLOCK_PADDING;

    switch (block.kind) {
      case 'dialog': {
        const style = SPEAKER_STYLE[block.speaker ?? 'system'] ?? SPEAKER_STYLE.system;
        if (style.label) {
          ctx.fillStyle = '#8a7a5a';
          ctx.font = 'bold 9px "Georgia", serif';
          ctx.textAlign = 'left';
          ctx.fillText(style.label, textX, textY);
          textY += 14;
        }
        ctx.fillStyle = style.color;
        ctx.font = style.font;
        ctx.textAlign = 'left';
        drawWrappedText(ctx, block.text ?? '', textX, textY, contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
        break;
      }

      case 'narration': {
        ctx.fillStyle = '#6a5a4a';
        ctx.font = 'italic 10px "Georgia", serif';
        ctx.textAlign = 'center';
        drawWrappedText(ctx, block.text ?? '', bx + contentW / 2, textY, contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
        break;
      }

      case 'option-group': {
        if (block.text) {
          ctx.fillStyle = '#4a3a2a';
          ctx.font = '11px "Georgia", serif';
          ctx.textAlign = 'left';
          const usedH = drawWrappedText(ctx, block.text, textX, textY, contentW - BLOCK_PADDING * 2, DIALOG_LINE_H);
          textY += usedH + 6;
        }
        // Draw each option
        if (block.options) {
          for (const opt of block.options) {
            drawOption(ctx, opt, block, textX, textY, contentW - BLOCK_PADDING * 2, hitRects, alpha);
            textY += OPTION_LINE_H;
          }
        }
        break;
      }

      case 'image': {
        // Placeholder frame — actual image loading is future work
        ctx.strokeStyle = '#a09080';
        ctx.lineWidth = 0.5;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(bx + 10, by + 10, contentW - 20, bh - 20);
        ctx.setLineDash([]);
        ctx.fillStyle = '#8a7a5a';
        ctx.font = 'italic 10px "Georgia", serif';
        ctx.textAlign = 'center';
        ctx.fillText(block.imageRef ?? '[image]', bx + contentW / 2, by + bh / 2);
        break;
      }

      case 'interactive': {
        // Bordered region where ink persists
        ctx.strokeStyle = '#6a4c93';
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 3]);
        ctx.strokeRect(bx + 6, by + 6, contentW - 12, bh - 12);
        ctx.setLineDash([]);
        // Label
        ctx.fillStyle = 'rgba(106, 76, 147, 0.4)';
        ctx.font = '8px "Georgia", serif';
        ctx.textAlign = 'right';
        ctx.fillText('✎ draw here', bx + contentW - 12, by + 16);
        break;
      }

      case 'separator': {
        ctx.strokeStyle = '#b0a080';
        ctx.lineWidth = 0.5;
        ctx.globalAlpha *= 0.5;
        const midY = by + bh / 2;
        ctx.beginPath();
        ctx.moveTo(bx + 20, midY);
        ctx.lineTo(bx + contentW - 20, midY);
        ctx.stroke();
        // Decorative diamond
        ctx.fillStyle = '#b0a080';
        ctx.beginPath();
        const dx = bx + contentW / 2;
        ctx.moveTo(dx, midY - 4);
        ctx.lineTo(dx + 4, midY);
        ctx.lineTo(dx, midY + 4);
        ctx.lineTo(dx - 4, midY);
        ctx.closePath();
        ctx.fill();
        break;
      }
    }

    // Timer bar (if block has a timer and is not yet resolved)
    if (block.timerMs > 0 && !block.resolved) {
      const elapsed = clockMs - block.appearedAt;
      const remaining = Math.max(0, block.timerMs - elapsed);
      const frac = remaining / block.timerMs;

      const timerY = by + bh - TIMER_ICON_SIZE - 6;
      const timerX = bx + contentW - TIMER_ICON_SIZE - 8;

      // Progress arc (clock face)
      ctx.strokeStyle = frac > 0.25 ? '#8a7a5a' : '#c04040';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(timerX + TIMER_ICON_SIZE / 2, timerY + TIMER_ICON_SIZE / 2,
              TIMER_ICON_SIZE / 2 - 2, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac);
      ctx.stroke();

      // Clock icon outline
      ctx.strokeStyle = '#8a7a5a';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(timerX + TIMER_ICON_SIZE / 2, timerY + TIMER_ICON_SIZE / 2,
              TIMER_ICON_SIZE / 2 - 2, 0, Math.PI * 2);
      ctx.stroke();

      // ⏩ skip label
      ctx.fillStyle = '#6a5a4a';
      ctx.font = '8px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('skip ▸', timerX - 4, timerY + TIMER_ICON_SIZE / 2 + 3);

      // Hit rect for skip clock
      hitRects.push({
        kind: 'clock-skip',
        blockId: block.id,
        x: timerX - 30, y: timerY,
        w: TIMER_ICON_SIZE + 34, h: TIMER_ICON_SIZE,
      });

      // Auto-resolve if timer has expired
      if (remaining <= 0) {
        block.resolved = true;
      }
    }

    ctx.restore();
  }

  // Location indicator (bottom-left, above newest block)
  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = '#5a4a3a';
  ctx.font = '9px "Georgia", serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  const loc = state.locations.find(l => l.id === state.env.location);
  if (loc) {
    ctx.fillText(`📍 ${loc.name}`, BLOCK_INSET, pageH - 8);
  }
  ctx.restore();

  return hitRects;
}

// ── Block background ─────────────────────────────────────────────────────────

function drawBlockBackground(
  ctx: CanvasRenderingContext2D,
  block: ActionBlock,
  x: number, y: number,
  w: number, h: number,
): void {
  const r = 4;

  // Subtle background fill
  let fill: string;
  switch (block.kind) {
    case 'dialog':
      fill = SPEAKER_STYLE[block.speaker ?? 'system']?.bgTint ?? 'rgba(180,170,150,0.06)';
      break;
    case 'narration':
      fill = 'rgba(160,150,130,0.05)';
      break;
    case 'option-group':
      fill = 'rgba(180,160,120,0.08)';
      break;
    case 'interactive':
      fill = 'rgba(106,76,147,0.04)';
      break;
    default:
      fill = 'rgba(0,0,0,0)';
  }

  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();

  // Resolved blocks get a faded overlay
  if (block.resolved) {
    ctx.fillStyle = 'rgba(200, 190, 170, 0.06)';
    ctx.fill();
  }

  // Thin left accent for dialog blocks
  if (block.kind === 'dialog' && block.speaker) {
    const accentColors: Record<string, string> = {
      ireth: '#6a5a40',
      laura: '#a08060',
      system: '#a09080',
    };
    ctx.fillStyle = accentColors[block.speaker] ?? '#a09080';
    ctx.fillRect(x, y + 4, 2, h - 8);
  }
}

// ── Option drawing ───────────────────────────────────────────────────────────

function drawOption(
  ctx: CanvasRenderingContext2D,
  opt: ActionBlockOption,
  block: ActionBlock,
  x: number, y: number,
  maxW: number,
  hitRects: ScrollHitRect[],
  alpha: number,
): void {
  const circleR = 7;
  const cx = x + circleR + 2;
  const cy = y + OPTION_LINE_H / 2;

  // Selection circle
  ctx.strokeStyle = opt.selected ? '#6a4c93' : '#8a7a5a';
  ctx.lineWidth = opt.selected ? 1.8 : 0.8;
  ctx.beginPath();
  ctx.arc(cx, cy, circleR, 0, Math.PI * 2);
  ctx.stroke();

  if (opt.selected) {
    ctx.fillStyle = 'rgba(106, 76, 147, 0.3)';
    ctx.fill();
    // Checkmark
    ctx.strokeStyle = '#6a4c93';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 3, cy);
    ctx.lineTo(cx - 1, cy + 3);
    ctx.lineTo(cx + 4, cy - 3);
    ctx.stroke();
  }

  // Option text
  ctx.fillStyle = opt.selected ? '#4a3060' : '#4a3a2a';
  ctx.font = opt.selected ? 'bold 10px "Georgia", serif' : '10px "Georgia", serif';
  ctx.textAlign = 'left';
  ctx.fillText(opt.text, cx + circleR + 6, cy + 3);

  // Hit rect (only if block isn't resolved — no double-picking)
  if (!block.resolved && alpha > 0.3) {
    hitRects.push({
      kind: 'option',
      blockId: block.id,
      optionId: opt.id,
      x: x, y: y,
      w: maxW, h: OPTION_LINE_H,
    });
  }
}

// ── Scroll hit test ──────────────────────────────────────────────────────────

/**
 * Check if a point hits a scroll interactive element.
 * Coordinates should be in page-local space (already translated).
 */
export function hitTestScroll(
  hitRects: ScrollHitRect[],
  px: number, py: number,
): ScrollHitRect | null {
  for (const r of hitRects) {
    if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) {
      return r;
    }
  }
  return null;
}
